import { useCallback, useEffect, useRef, useState } from 'react';
import type { ColumnDefinition } from '@/components/shared/column-config-dialog';
import { useMode } from '@/providers/ModeProvider';
import { useApi } from './useApi';

interface ColumnConfigApiResponse {
  screenName: string;
  columnId: string;
  isVisible: boolean;
  order: number;
  width?: number | null;
  pinnedDirection?: string | null;
}

export interface GridLayout {
  columnOrder: string[];
  columnWidths: Record<string, number>;
  pinnedLeft: string[];
  pinnedRight: string[];
}

const EMPTY_LAYOUT: GridLayout = {
  columnOrder: [],
  columnWidths: {},
  pinnedLeft: [],
  pinnedRight: [],
};

function mergeApiWithDefaults(apiConfigs: ColumnConfigApiResponse[], defaults: ColumnDefinition[]): ColumnDefinition[] {
  const defaultIds = new Set(defaults.map((col) => col.id));

  const filteredApiConfigs = apiConfigs.filter((apiCol) => defaultIds.has(apiCol.columnId));
  const filteredApiMap = new Map(filteredApiConfigs.map((x) => [x.columnId, x]));

  return defaults
    .map((defaultCol) => {
      const apiCol = filteredApiMap.get(defaultCol.id);
      if (apiCol) {
        return {
          ...defaultCol,
          visible: defaultCol.locked ? defaultCol.visible : apiCol.isVisible,
          order: apiCol.order,
        };
      }
      return defaultCol;
    })
    .sort((a, b) => a.order - b.order);
}

function extractGridLayoutFromApi(apiConfigs: ColumnConfigApiResponse[]): GridLayout {
  const widths: Record<string, number> = {};
  const pinnedLeft: string[] = [];
  const pinnedRight: string[] = [];

  for (const config of apiConfigs) {
    if (config.width != null) {
      widths[config.columnId] = config.width;
    }
    if (config.pinnedDirection === 'left') {
      pinnedLeft.push(config.columnId);
    } else if (config.pinnedDirection === 'right') {
      pinnedRight.push(config.columnId);
    }
  }

  const columnOrder = [...apiConfigs].sort((a, b) => a.order - b.order).map((x) => x.columnId);

  return { columnOrder, columnWidths: widths, pinnedLeft, pinnedRight };
}

function mergeWithDefaults(stored: ColumnDefinition[], defaults: ColumnDefinition[]): ColumnDefinition[] {
  const defaultIds = new Set(defaults.map((col) => col.id));

  const filteredStored = stored.filter((col) => defaultIds.has(col.id));
  const storedMap = new Map(filteredStored.map((col) => [col.id, col]));

  return defaults
    .map((defaultCol) => {
      const storedCol = storedMap.get(defaultCol.id);
      if (storedCol) {
        return {
          ...defaultCol,
          visible: defaultCol.locked ? defaultCol.visible : storedCol.visible,
          order: storedCol.order,
        };
      }
      return defaultCol;
    })
    .sort((a, b) => a.order - b.order);
}

function loadColumnsFromLocalStorage(storageKey: string, defaults: ColumnDefinition[]): ColumnDefinition[] {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored) as ColumnDefinition[];
      return mergeWithDefaults(parsed, defaults);
    }
  } catch {
    console.warn(`Failed to load column preferences from localStorage for key: ${storageKey}`);
  }
  return defaults;
}

function loadGridLayoutFromLocalStorage(storageKey: string): GridLayout {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<GridLayout>;
      return {
        columnOrder: Array.isArray(parsed.columnOrder) ? parsed.columnOrder : [],
        columnWidths: parsed.columnWidths && typeof parsed.columnWidths === 'object' ? parsed.columnWidths : {},
        pinnedLeft: Array.isArray(parsed.pinnedLeft) ? parsed.pinnedLeft : [],
        pinnedRight: Array.isArray(parsed.pinnedRight) ? parsed.pinnedRight : [],
      };
    }
  } catch {
    // Ignore parse errors
  }
  return EMPTY_LAYOUT;
}

function buildApiPayload(columnsToSave: ColumnDefinition[], layout: GridLayout, screenName: string) {
  return {
    screenName,
    columns: columnsToSave.map((col, index) => ({
      columnId: col.id,
      isVisible: col.visible,
      order:
        layout.columnOrder.length > 0 && layout.columnOrder.indexOf(col.id) >= 0
          ? layout.columnOrder.indexOf(col.id)
          : index,
      width: layout.columnWidths[col.id] ?? null,
      pinnedDirection: layout.pinnedLeft.includes(col.id)
        ? 'left'
        : layout.pinnedRight.includes(col.id)
          ? 'right'
          : null,
    })),
  };
}

export function useScreenColumnPreferences(screenName: string, defaultColumns: ColumnDefinition[]) {
  const columnsStorageKey = `${screenName}-column-preferences`;
  const gridLayoutStorageKey = `${screenName}-grid-layout`;

  const [columns, setColumns] = useState<ColumnDefinition[]>(() =>
    loadColumnsFromLocalStorage(columnsStorageKey, defaultColumns),
  );
  const [gridLayout, setGridLayout] = useState<GridLayout>(() => loadGridLayoutFromLocalStorage(gridLayoutStorageKey));
  const [isLoading, setIsLoading] = useState(true);
  const { apiGet, apiBaseUrl, getHeaders } = useApi();
  const { isDemo } = useMode();
  const apiLoadedRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const columnsRef = useRef(columns);
  columnsRef.current = columns;
  const gridLayoutRef = useRef(gridLayout);
  gridLayoutRef.current = gridLayout;

  useEffect(() => {
    if (isDemo) {
      setIsLoading(false);
      return;
    }

    if (apiLoadedRef.current) return;
    let cancelled = false;

    async function loadFromApi() {
      try {
        const response = await apiGet<ColumnConfigApiResponse[]>(
          `/api/v1/columnConfig?screenName=${encodeURIComponent(screenName)}`,
        );
        if (cancelled) return;

        const screenConfigs = response;
        if (screenConfigs.length > 0) {
          const merged = mergeApiWithDefaults(screenConfigs, defaultColumns);
          setColumns(merged);
          localStorage.setItem(columnsStorageKey, JSON.stringify(merged));

          const layout = extractGridLayoutFromApi(screenConfigs);
          setGridLayout(layout);
          localStorage.setItem(gridLayoutStorageKey, JSON.stringify(layout));
        }
        apiLoadedRef.current = true;
      } catch {
        // Will retry when apiGet changes
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadFromApi();
    return () => {
      cancelled = true;
    };
  }, [apiGet, screenName, defaultColumns, columnsStorageKey, gridLayoutStorageKey, isDemo]);

  const saveToApi = useCallback(
    async (columnsToSave: ColumnDefinition[], layoutToSave: GridLayout) => {
      if (isDemo) return;

      const headers = await getHeaders();
      const payload = buildApiPayload(columnsToSave, layoutToSave, screenName);
      const response = await fetch(`${apiBaseUrl}/api/v1/columnConfig`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`Failed to save column config: ${response.status}`);
      }
    },
    [apiBaseUrl, getHeaders, screenName, isDemo],
  );

  const persistGridLayoutToLocalStorage = useCallback(
    (layout: GridLayout) => {
      try {
        localStorage.setItem(gridLayoutStorageKey, JSON.stringify(layout));
      } catch {
        // Ignore quota errors
      }
    },
    [gridLayoutStorageKey],
  );

  const savePreferences = useCallback(
    async (newColumns: ColumnDefinition[]) => {
      setColumns(newColumns);
      columnsRef.current = newColumns;
      try {
        localStorage.setItem(columnsStorageKey, JSON.stringify(newColumns));
      } catch {
        console.warn('Failed to cache column preferences to localStorage');
      }

      // Sync gridLayout.columnOrder to match the dialog's new order
      const newOrder = [...newColumns].sort((a, b) => a.order - b.order).map((col) => col.id);
      const updatedLayout = { ...gridLayoutRef.current, columnOrder: newOrder };
      setGridLayout(updatedLayout);
      gridLayoutRef.current = updatedLayout;
      persistGridLayoutToLocalStorage(updatedLayout);

      try {
        await saveToApi(newColumns, updatedLayout);
      } catch (error) {
        console.error('Failed to save column preferences to API:', error);
      }
    },
    [saveToApi, columnsStorageKey, persistGridLayoutToLocalStorage],
  );

  const resetToDefaults = useCallback(async () => {
    setColumns(defaultColumns);
    const emptyLayout = { ...EMPTY_LAYOUT };
    setGridLayout(emptyLayout);
    try {
      localStorage.removeItem(columnsStorageKey);
      localStorage.removeItem(gridLayoutStorageKey);
    } catch {
      console.warn('Failed to clear column preferences from localStorage');
    }
    try {
      await saveToApi(defaultColumns, emptyLayout);
    } catch (error) {
      console.error('Failed to reset column preferences via API:', error);
    }
  }, [saveToApi, columnsStorageKey, gridLayoutStorageKey, defaultColumns]);

  const setColumnOrder = useCallback(
    (orderOrUpdater: string[] | ((prev: string[]) => string[])) => {
      setGridLayout((prev) => {
        const newOrder = typeof orderOrUpdater === 'function' ? orderOrUpdater(prev.columnOrder) : orderOrUpdater;
        const updated = { ...prev, columnOrder: newOrder };
        persistGridLayoutToLocalStorage(updated);

        // Sync columns state so Configure Columns dialog reflects drag order
        if (newOrder.length > 0) {
          const reorderedColumns = columnsRef.current
            .map((col) => {
              const idx = newOrder.indexOf(col.id);
              return { ...col, order: idx >= 0 ? idx : col.order };
            })
            .sort((a, b) => a.order - b.order);
          columnsRef.current = reorderedColumns;
          setColumns(reorderedColumns);
          try {
            localStorage.setItem(columnsStorageKey, JSON.stringify(reorderedColumns));
          } catch {
            /* ignore */
          }
        }

        saveToApi(columnsRef.current, updated).catch(() => {});
        return updated;
      });
    },
    [persistGridLayoutToLocalStorage, saveToApi, columnsStorageKey],
  );

  const setColumnWidths = useCallback(
    (widthsOrUpdater: Record<string, number> | ((prev: Record<string, number>) => Record<string, number>)) => {
      setGridLayout((prev) => {
        const newWidths = typeof widthsOrUpdater === 'function' ? widthsOrUpdater(prev.columnWidths) : widthsOrUpdater;
        const updated = { ...prev, columnWidths: newWidths };
        persistGridLayoutToLocalStorage(updated);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          saveToApi(columnsRef.current, updated).catch(() => {});
        }, 300);
        return updated;
      });
    },
    [persistGridLayoutToLocalStorage, saveToApi],
  );

  const setPinned = useCallback(
    (newPinnedLeft: string[], newPinnedRight: string[]) => {
      setGridLayout((prev) => {
        const updated = { ...prev, pinnedLeft: newPinnedLeft, pinnedRight: newPinnedRight };
        persistGridLayoutToLocalStorage(updated);
        saveToApi(columnsRef.current, updated).catch(() => {});
        return updated;
      });
    },
    [persistGridLayoutToLocalStorage, saveToApi],
  );

  const resetColumnOrder = useCallback(() => {
    const resetColumns = columnsRef.current
      .map((col) => {
        const defaultIndex = defaultColumns.findIndex((d) => d.id === col.id);
        return { ...col, order: defaultIndex >= 0 ? defaultIndex : col.order };
      })
      .sort((a, b) => a.order - b.order);
    setColumns(resetColumns);
    try {
      localStorage.setItem(columnsStorageKey, JSON.stringify(resetColumns));
    } catch {
      // Ignore quota errors
    }
    columnsRef.current = resetColumns;
    setGridLayout((prev) => {
      const updated = { ...prev, columnOrder: [], pinnedLeft: [], pinnedRight: [] };
      persistGridLayoutToLocalStorage(updated);
      saveToApi(resetColumns, updated).catch(() => {});
      return updated;
    });
  }, [persistGridLayoutToLocalStorage, saveToApi, defaultColumns, columnsStorageKey]);

  const resetColumnWidths = useCallback(() => {
    setGridLayout((prev) => {
      const updated = { ...prev, columnWidths: {} };
      persistGridLayoutToLocalStorage(updated);
      saveToApi(columnsRef.current, updated).catch(() => {});
      return updated;
    });
  }, [persistGridLayoutToLocalStorage, saveToApi]);

  const resetLayout = useCallback(() => {
    const emptyLayout = { ...EMPTY_LAYOUT };
    setGridLayout(emptyLayout);
    try {
      localStorage.removeItem(gridLayoutStorageKey);
    } catch {
      // Ignore
    }
    saveToApi(columnsRef.current, emptyLayout).catch(() => {});
  }, [gridLayoutStorageKey, saveToApi]);

  const visibleColumns = columns.filter((col) => col.visible).sort((a, b) => a.order - b.order);

  return {
    columns,
    visibleColumns,
    isLoading,
    savePreferences,
    resetToDefaults,
    columnOrder: gridLayout.columnOrder,
    columnWidths: gridLayout.columnWidths,
    pinnedLeft: gridLayout.pinnedLeft,
    pinnedRight: gridLayout.pinnedRight,
    setColumnOrder,
    setColumnWidths,
    setPinned,
    resetColumnOrder,
    resetColumnWidths,
    resetLayout,
  };
}
